import React, { useState, useEffect } from "react";
import API_CLIENT from "../../lib/api-client";
import { GET_ALL_CATEGORIES } from "../../utils/constant";
import Loading from "../../component/loading";
import { Link } from "react-router-dom";
import { CategoryCardSkeleton } from '../../component/Skeleton';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await API_CLIENT.get(GET_ALL_CATEGORIES);
        if (response.status === 201) {
          setCategories(response.data.categories);
          setFilteredCategories(response.data.categories);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Filter categories when search query changes
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (category.subDescription && 
         typeof category.subDescription === 'string' &&
         category.subDescription.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredCategories(filtered);
    }
  }, [searchQuery, categories]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Search Bar */}
      <div className="mb-8">
        <div className="max-w-md mx-auto">
          <div className="relative flex items-center">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search categories..."
              className="w-full px-4 py-3 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 text-gray-400"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          
          {/* Search results summary */}
          {searchQuery.trim() !== "" && (
            <p className="mt-2 text-sm text-gray-600">
              Found {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'} for "{searchQuery}"
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <CategoryCardSkeleton key={index} />
          ))}
        </div>
      ) : (
        <>
          {filteredCategories.length === 0 && searchQuery.trim() !== "" ? (
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-lg p-8 max-w-md mx-auto">
                <p className="text-lg text-gray-600 mb-2">No categories found matching "{searchQuery}"</p>
                <button
                  onClick={clearSearch}
                  className="mt-2 text-teal-600 hover:text-teal-800 transition-colors"
                >
                  Clear search
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredCategories.map((category) => (
                <div 
                  key={category._id} 
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <Link 
                    to={`/categoryDetails/${category._id}`}
                    className="block"
                  >
                    <div className="aspect-w-16 aspect-h-9">
                      <img 
                        src={category.imageUrl} 
                        alt={category.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-gray-800 hover:text-blue-600">
                        {category.name}
                      </h3>
                      {category.subDescription && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                          {category.subDescription}
                        </p>
                      )}
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Category;
